import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
    setMenuOpen(false);
  };

  const navLinks = [
    { to: "/services", label: "Services" },
    { to: "/about", label: "About" },
    { to: "/insights", label: "Insights" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <nav className="bg-navy text-ivory sticky top-0 z-50 shadow-lg" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group" data-testid="nav-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_beyond-stuck/artifacts/9pqx1i8e_c56df822-758c-4f8c-8142-6dfb845706a4.jpg"
              alt="De Nicola Consulting Co."
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="text-ivory/80 hover:text-gold text-sm tracking-[0.1em] uppercase transition-colors duration-300 font-lora"
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
            {user ? (
              <div className="flex items-center gap-4">
                {user.role === "admin" && (
                  <Link to="/admin" className="text-gold text-sm tracking-widest uppercase hover:opacity-80 transition-opacity font-lora" data-testid="nav-admin">Admin</Link>
                )}
                <Link to="/dashboard" className="text-ivory/80 hover:text-gold text-sm tracking-widest uppercase transition-colors font-lora" data-testid="nav-dashboard">Dashboard</Link>
                <button
                  onClick={handleLogout}
                  className="text-xs tracking-widest uppercase text-ivory/60 hover:text-gold transition-colors"
                  data-testid="nav-logout"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <Link to="/login" className="text-ivory/80 hover:text-gold text-sm tracking-widest uppercase transition-colors font-lora" data-testid="nav-login">Sign In</Link>
                <Link
                  to="/booking"
                  className="bg-gold text-navy px-5 py-2 text-xs tracking-[0.15em] uppercase font-medium hover:bg-gold/90 transition-colors"
                  data-testid="nav-book-cta"
                >
                  Book Session
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden text-ivory p-2"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="nav-mobile-toggle"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-navy border-t border-white/10 px-6 py-4 flex flex-col gap-4">
          {navLinks.map(link => (
            <Link key={link.to} to={link.to} onClick={() => setMenuOpen(false)}
              className="text-ivory/80 hover:text-gold text-sm tracking-widest uppercase font-lora">
              {link.label}
            </Link>
          ))}
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" onClick={() => setMenuOpen(false)} className="text-gold text-sm tracking-widest uppercase font-lora">Admin</Link>
              )}
              <Link to="/dashboard" onClick={() => setMenuOpen(false)} className="text-ivory/80 text-sm tracking-widest uppercase font-lora">Dashboard</Link>
              <button onClick={handleLogout} className="text-left text-ivory/60 text-sm tracking-widest uppercase">Sign Out</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setMenuOpen(false)} className="text-ivory/80 text-sm tracking-widest uppercase font-lora">Sign In</Link>
              <Link to="/booking" onClick={() => setMenuOpen(false)} className="bg-gold text-navy px-4 py-2 text-xs tracking-widest uppercase text-center">Book Session</Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
